import { z } from 'zod'

import { slugFromPathname } from './basePath.ts'

/** A tenant slug is what appears in a subdomain or a `?tenant=` parameter. */
export const tenantSlugSchema = z
  .string()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Expected a lowercase DNS-safe slug')

export type TenantSlug = z.infer<typeof tenantSlugSchema>

export type TenantSource = 'path' | 'subdomain' | 'search-param' | 'fallback'

export interface TenantContextValue {
  /** Slug resolved from the current URL, or the configured fallback. */
  readonly slug: TenantSlug
  /** Where the slug came from — useful when debugging a wrong-tenant report. */
  readonly source: TenantSource
  /**
   * Tenant *records* (name, branding, working hours) are loaded from the
   * backend in stage 6. Until then nothing beyond the slug is known.
   */
  readonly isProfileLoaded: false
}

const RESERVED_SUBDOMAINS = new Set(['www', 'app', 'api', 'admin', 'localhost'])

function subdomainOf(hostname: string): string | null {
  const labels = hostname.split('.')
  // Needs at least sub.domain.tld; bare hosts and IPs carry no tenant.
  if (labels.length < 3) return null

  const candidate = labels[0]
  if (candidate === undefined || RESERVED_SUBDOMAINS.has(candidate)) return null

  return candidate
}

/**
 * Pure URL -> tenant resolution, so it can be unit-tested without a browser.
 */
export function resolveTenant(
  url: URL,
  fallbackSlug: string,
): TenantContextValue {
  // /s/<slug>/ is the studio's real address and outranks everything else.
  const fromPath = tenantSlugSchema.safeParse(slugFromPathname(url.pathname))
  if (fromPath.success) {
    return { slug: fromPath.data, source: 'path', isProfileLoaded: false }
  }

  const fromSearch = url.searchParams.get('tenant')
  const searchCandidate = tenantSlugSchema.safeParse(fromSearch)
  if (searchCandidate.success) {
    return { slug: searchCandidate.data, source: 'search-param', isProfileLoaded: false }
  }

  const subdomainCandidate = tenantSlugSchema.safeParse(subdomainOf(url.hostname))
  if (subdomainCandidate.success) {
    return { slug: subdomainCandidate.data, source: 'subdomain', isProfileLoaded: false }
  }

  const fallback = tenantSlugSchema.parse(fallbackSlug)
  return { slug: fallback, source: 'fallback', isProfileLoaded: false }
}
